import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  Icon,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useBreakpointValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { format, parseISO, endOfDay, isBefore } from 'date-fns';
import { useListClientServices } from 'hooks/useApiQueries';
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import { ClientService } from '../../../types/client';

export function ServicesHistory(): JSX.Element {
  const { data: servicesData } = useListClientServices();
  const services = servicesData ? Object.values(servicesData) : [];
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Détecter si on vient d'un paiement Alma réussi
  const searchParams = new URLSearchParams(location.search);
  const almaSuccess = searchParams.get('alma_success') === '1';
  const [showAlmaSuccessMessage, setShowAlmaSuccessMessage] = useState(almaSuccess);

  const { activeServices, expiredServices } = useMemo(() => {
    if (!services) return { activeServices: [], expiredServices: [] };

    const active: ClientService[] = [];
    const expired: ClientService[] = [];

    services.forEach((service: ClientService) => {
      // Un crédit est expiré si sa date d'expiration est avant la fin de la journée actuelle
      // Cela permet d'inclure les crédits qui expirent aujourd'hui comme actifs
      if (isBefore(endOfDay(parseISO(service.ExpirationDate)), new Date())) {
        expired.push(service);
      } else {
        active.push(service);
      }
    });

    return {
      activeServices: active.sort((a, b) => new Date(b.ActiveDate).getTime() - new Date(a.ActiveDate).getTime()),
      expiredServices: expired.sort((a, b) => new Date(b.ActiveDate).getTime() - new Date(a.ActiveDate).getTime()),
    };
  }, [services]);

  // Nettoyer l'URL une fois les services chargés
  useEffect(() => {
    if (almaSuccess && services.length > 0) {
      // Retirer le paramètre alma_success de l'URL après un délai
      const timer = setTimeout(() => {
        setShowAlmaSuccessMessage(false);
        // Nettoyer l'URL
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('alma_success');
        const newSearch = newSearchParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        window.history.replaceState({}, '', newUrl);
      }, 5000); // Afficher le message pendant 5 secondes

      return () => clearTimeout(timer);
    }
  }, [almaSuccess, services.length, location.search, location.pathname]);

  if (!services || services.length === 0) {
    return (
      <Box className="accountBox" w="full" p={8} textAlign="center">
        <HStack width="100%" justifyContent="space-between" margin="1em 0 2em">
          <Heading className="accountMainTitle" size="md" textTransform="uppercase">
            {t('Mes Crédits')}
          </Heading>
          <Button variant="contained" onClick={() => navigate(-1)}>
            Retour
          </Button>
        </HStack>

        <VStack spacing={6} py={8}>
          <Heading size="lg" color="gray.600">
            Vos crédits sont en cours de traitement
          </Heading>

          <VStack spacing={3} maxW="md" mx="auto">
            <Text fontSize="lg" color="gray.600">
              ✅ Votre achat a été confirmé avec succès !
            </Text>
            <Text color="gray.500" textAlign="center">
              Vos crédits peuvent prendre <strong>quelques minutes</strong> à apparaître dans votre compte.
              N'hésitez pas à actualiser cette page dans quelques instants.
            </Text>
          </VStack>

          <HStack spacing={4} pt={4}>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              colorScheme="gray"
            >
              🔄 Actualiser
            </Button>
            <Button variant="contained" onClick={() => navigate('/tarifs')}>
              Acheter d'autres crédits
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              Réserver un cours
            </Button>
          </HStack>

          <Text fontSize="sm" color="gray.400" pt={4}>
            Si vos crédits n'apparaissent pas après 10 minutes, contactez notre support.
          </Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Stack className="activeServicesHistory">
      {activeServices.length > 0 && (
        <Box my={4}>
          <HStack width="100%" justifyContent="space-between" margin="1em 0 2em">
            <Heading className="accountMainTitle" size="md" textTransform="uppercase">
              {t('Crédits Actifs')}
            </Heading>
            <Button variant="contained" onClick={() => navigate(-1)}>
              Retour
            </Button>
          </HStack>

          {/* Message de succès Alma */}
          {showAlmaSuccessMessage && (
            <Alert
              status="success"
              variant="subtle"
              borderRadius="lg"
              mb={6}
              bg="green.50"
              borderColor="green.200"
              borderWidth="1px"
            >
              <AlertIcon color="green.500" />
              <VStack align="start" spacing={1} flex="1">
                <AlertTitle color="green.800" fontWeight="bold">
                  🎉 Paiement Alma confirmé !
                </AlertTitle>
                <AlertDescription color="green.700" fontSize="sm">
                  Vos crédits ont été ajoutés avec succès à votre compte.
                  <br />
                  Si vous ne voyez pas vos nouveaux crédits, ils peuvent prendre quelques minutes à s'afficher.
                </AlertDescription>
              </VStack>
              <Button
                size="sm"
                variant="outline"
                colorScheme="green"
                onClick={() => window.location.reload()}
                ml={2}
              >
                🔄 Actualiser
              </Button>
            </Alert>
          )}

          <Services services={activeServices} />
        </Box>
      )}

      {expiredServices.length > 0 && activeServices.length > 0 && <Divider />}

      {expiredServices.length > 0 && (
        <Box my={4} className="expiredServicesHistory">
          <Heading textTransform="uppercase" size="md">
            {t('Crédits Expirés')}
          </Heading>
          <Services services={expiredServices} />
        </Box>
      )}
    </Stack>
  );
}

function Services({ services }: { services: ClientService[] }) {
  const { t } = useTranslation();

  const isMobile = useBreakpointValue({ base: true, md: false }) || false;
  const [showDetails, setShowDetails] = useState<Set<string>>(new Set());

  const handleToggleDetails = (serviceId: string) => {
    setShowDetails((prevState) => {
      const newSet = new Set(prevState); // Crée une copie du Set actuel
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId); // Si l'ID est déjà présent, le supprimer (fermer les détails)
      } else {
        newSet.add(serviceId); // Sinon, ajouter l'ID (ouvrir les détails)
      }
      return newSet;
    });
  };

  const rotateIcon = showDetails ? 'rotate(180deg)' : 'rotate(0)';

  return (
    <Table variant="simple">
      <Thead>
        <Tr>
          <Th>{t('Nom du pack')}</Th>
          <Th isNumeric>{t('Sessions restantes')}</Th>
          {!isMobile && (
            <>
              <Th>{t("Date d'activation")}</Th>
              <Th>{t("Date d'expiration")}</Th>
            </>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {services.map((service, index) => (
          <React.Fragment key={index}>
            <Tr>
              <Td>{service.Name}</Td>
              <Td isNumeric>
                {service.Remaining}
                {isMobile && (
                  <Button onClick={() => handleToggleDetails(service.Id.toString())} variant="ghost" size="md">
                    <Icon as={ChevronDownIcon} transition="transform .2s ease-in-out" transform={rotateIcon} />
                  </Button>
                )}
              </Td>
              {!isMobile && (
                <>
                  <Td>{format(parseISO(service.ActiveDate), 'dd/MM/yyyy')}</Td>
                  <Td>{format(parseISO(service.ExpirationDate), 'dd/MM/yyyy')}</Td>
                </>
              )}
            </Tr>
            {isMobile && showDetails.has(service.Id.toString()) && (
              <Tr key={`details-${service.Id}`}>
                <Td colSpan={4}>
                  <VStack align="start" spacing={2}>
                    <Text>
                      <strong>Date d'activation :</strong> {format(parseISO(service.ActiveDate), 'dd/MM/yyyy')}
                    </Text>
                    <Text>
                      <strong>Date d'expiration :</strong> {format(parseISO(service.ExpirationDate), 'dd/MM/yyyy')}
                    </Text>
                  </VStack>
                </Td>
              </Tr>
            )}
          </React.Fragment>
        ))}
      </Tbody>
    </Table>
  );
}
